/**
 * AGOLDataService - Servicio para datos ArcGIS Online de Andalucía
 *
 * Proporciona acceso a 36K+ features de infraestructuras procedentes de AGOL:
 * - 7 categorías: patrimonio, servicios, transporte, hidrografía,
 *   infraestructuras energéticas, tejido económico, sistema urbano
 * - Búsqueda por proximidad a coordenadas UTM30
 * - Filtrado por categoría, subcategoría, capa y tipo
 * - Búsqueda por nombre (case-insensitive)
 * - Caché LRU por municipio
 *
 * Datos: ArcGIS Online - Junta de Andalucía (GREA)
 * CRS: EPSG:25830 (UTM30 ETRS89)
 *
 * @module services/data/AGOLDataService
 * @version 1.0.0
 * @date 2026-02-09
 */

import { fetchWithBackoff } from '../../utils/fetchWithBackoff';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 para datos AGOL (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 */
const R2_BASE_URL = WORKER_BASE;

/** URL base para bundle estático local (fallback) */
const STATIC_BUNDLE_BASE = '/data/agol';

/** Timeout para peticiones al Worker R2 (ms) */
const R2_TIMEOUT = 8_000;

/** Máximo de municipios en caché simultáneamente */
const MAX_CACHED_MUNICIPALITIES = 30;

/** Mapa de códigos de categoría AGOL a nombres */
export const AGOL_CATEGORIES: Record<string, string> = {
  '01': 'patrimonio',
  '02': 'servicios',
  '03': 'transporte_y_comunicaciones',
  '04': 'hidrografia',
  '05': 'infraestructuras_energeticas_y_medioambientales',
  '06': 'tejido_economico_y_productivo',
  '07': 'sistema_urbano',
};

// =============================================================================
// TIPOS
// =============================================================================

/** Geometría GeoJSON de un feature AGOL */
export interface AGOLGeometry {
  type: 'Point' | 'MultiPoint' | 'Polygon' | 'MultiPolygon' | 'LineString' | 'MultiLineString';
  coordinates: number[] | number[][] | number[][][] | number[][][][];
}

/** Propiedades de un feature AGOL */
export interface AGOLProperties {
  fid_original: number;
  id_original: string | null;
  nombre: string;
  tipo: string;
  cod_mun: string;
  municipio: string;
  provincia: string;
  agol_capa: string;
  agol_categoria: string;
  agol_categoria_nombre: string;
  agol_subcategoria: string | null;
  nom_unesco: string | null;
  estado: string | null;
  tipo_alt: string | null;
  subtipo: string | null;
  id_dera: string | null;
  localidad: string | null;
  direccion: string | null;
  [key: string]: unknown;
}

/** Feature individual AGOL (GeoJSON) */
export interface AGOLFeature {
  type: 'Feature';
  properties: AGOLProperties;
  geometry: AGOLGeometry;
}

/** FeatureCollection AGOL completa (respuesta R2) */
export interface AGOLFeatureCollection {
  type: 'FeatureCollection';
  name: string;
  features: AGOLFeature[];
}

/** Categoría AGOL con conteo de features */
export interface AGOLCategory {
  code: string;
  name: string;
  featureCount: number;
}

/** Dataset AGOL procesado y cacheado por municipio */
export interface AGOLDataset {
  codINE: string;
  features: AGOLFeature[];
  count: number;
  categories: AGOLCategory[];
  loadedAt: number;
}

/** Opciones de filtrado para consultas AGOL */
export interface AGOLFilterOptions {
  categoria?: string;
  subcategoria?: string;
  capa?: string;
  tipo?: string;
}

/** Resultado de búsqueda por proximidad */
export interface AGOLProximityResult {
  feature: AGOLFeature;
  distance: number;
}

/** Estadísticas del servicio AGOL */
export interface AGOLServiceStats {
  municipiosLoaded: number;
  totalFeatures: number;
  loadedCodINEs: string[];
}

// =============================================================================
// SERVICIO PRINCIPAL
// =============================================================================

export class AGOLDataService {
  private cache = new Map<string, AGOLDataset>();
  private accessOrder: string[] = [];

  /**
   * Carga datos AGOL de un municipio por código INE.
   * Cachea el resultado para accesos posteriores.
   */
  async loadMunicipality(codINE: string): Promise<AGOLDataset> {
    const cached = this.cache.get(codINE);
    if (cached) {
      this.updateAccessOrder(codINE);
      return cached;
    }

    const collection = await this.fetchMunicipalityData(codINE);
    const safeFeatures = Array.isArray(collection?.features) ? collection.features : [];
    const categories = this.extractCategories(safeFeatures);

    const dataset: AGOLDataset = {
      codINE,
      features: safeFeatures,
      count: safeFeatures.length,
      categories,
      loadedAt: Date.now(),
    };

    this.addToCache(codINE, dataset);
    console.log(
      `[AGOLDataService] Cargado ${codINE}: ${dataset.count} features, ` +
      `${categories.length} categorías`
    );
    return dataset;
  }

  /**
   * Descarga datos de municipio: R2 primero, fallback a estático
   */
  private async fetchMunicipalityData(codINE: string): Promise<AGOLFeatureCollection> {
    // Intento 1: R2 Worker
    try {
      const url = `${R2_BASE_URL}/agol/${codINE}.json.gz`;
      const { response } = await fetchWithBackoff(url, { timeoutMs: R2_TIMEOUT });

      if (response.ok) {
        return await response.json() as AGOLFeatureCollection;
      }
    } catch (error) {
      console.warn(`[AGOLDataService] R2 falló para ${codINE}:`, error);
    }

    // Intento 2: Bundle estático local
    try {
      const url = `${STATIC_BUNDLE_BASE}/${codINE}.json.gz`;
      const response = await fetch(url);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('application/json')) {
        return await response.json() as AGOLFeatureCollection;
      }

      // Intentar descomprimir gzip
      if (response.body) {
        const decompressed = response.body.pipeThrough(new DecompressionStream('gzip'));
        return await new Response(decompressed).json() as AGOLFeatureCollection;
      }

      throw new Error('Respuesta sin body');
    } catch (error) {
      console.error(`[AGOLDataService] Fallback estático falló para ${codINE}:`, error);
    }

    throw new Error(`No se pudieron cargar datos AGOL para municipio ${codINE}`);
  }

  /**
   * Extrae categorías únicas del dataset con conteo
   */
  private extractCategories(features: AGOLFeature[]): AGOLCategory[] {
    const catMap = new Map<string, { name: string; count: number }>();
    const featureArray = Array.isArray(features) ? features : [];

    for (const f of featureArray) {
      const code = f.properties.agol_categoria;
      const name = f.properties.agol_categoria_nombre ?? AGOL_CATEGORIES[code] ?? code;
      const entry = catMap.get(code);
      if (entry) {
        entry.count++;
      } else {
        catMap.set(code, { name, count: 1 });
      }
    }

    return Array.from(catMap.entries())
      .map(([code, { name, count }]) => ({ code, name, featureCount: count }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }

  /**
   * Obtiene features de un municipio con filtros opcionales
   */
  async getFeatures(codINE: string, options?: AGOLFilterOptions): Promise<AGOLFeature[]> {
    const dataset = await this.loadMunicipality(codINE);
    if (!options) return dataset.features;

    return dataset.features.filter(f => {
      const p = f.properties;
      if (options.categoria && p.agol_categoria !== options.categoria) return false;
      if (options.subcategoria && p.agol_subcategoria !== options.subcategoria) return false;
      if (options.capa && p.agol_capa !== options.capa) return false;
      if (options.tipo && p.tipo !== options.tipo) return false;
      return true;
    });
  }

  /**
   * Obtiene las categorías disponibles para un municipio
   */
  async getCategories(codINE: string): Promise<AGOLCategory[]> {
    const dataset = await this.loadMunicipality(codINE);
    return dataset.categories;
  }

  /**
   * Búsqueda por proximidad a coordenadas UTM30.
   * Solo funciona con features de tipo Point.
   */
  async searchByProximity(
    codINE: string,
    x: number,
    y: number,
    maxDistance = 500,
    options?: AGOLFilterOptions
  ): Promise<AGOLProximityResult[]> {
    const features = await this.getFeatures(codINE, options);
    const results: AGOLProximityResult[] = [];

    for (const feature of features) {
      if (feature.geometry.type !== 'Point') continue;
      const coords = feature.geometry.coordinates as number[];
      const dx = coords[0] - x;
      const dy = coords[1] - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= maxDistance) {
        results.push({ feature, distance });
      }
    }

    return results.sort((a, b) => a.distance - b.distance);
  }

  /**
   * Busca features por nombre (coincidencia parcial, case-insensitive)
   */
  async searchByName(
    codINE: string,
    query: string,
    options?: AGOLFilterOptions
  ): Promise<AGOLFeature[]> {
    const features = await this.getFeatures(codINE, options);
    const normalizedQuery = query.toUpperCase().trim();

    return features.filter(f =>
      f.properties.nombre?.toUpperCase().includes(normalizedQuery)
    );
  }

  /** Estadísticas del servicio */
  getStats(): AGOLServiceStats {
    let totalFeatures = 0;
    const loadedCodINEs: string[] = [];
    this.cache.forEach((data, codINE) => {
      loadedCodINEs.push(codINE);
      totalFeatures += data.count;
    });
    return { municipiosLoaded: this.cache.size, totalFeatures, loadedCodINEs };
  }

  /** Verifica si un municipio está cargado */
  isMunicipalityLoaded(codINE: string): boolean {
    return this.cache.has(codINE);
  }

  /** Limpia la caché */
  clearCache(): void {
    this.cache.clear();
    this.accessOrder = [];
  }

  // ===========================================================================
  // CACHÉ LRU
  // ===========================================================================

  private updateAccessOrder(codINE: string): void {
    const idx = this.accessOrder.indexOf(codINE);
    if (idx > -1) this.accessOrder.splice(idx, 1);
    this.accessOrder.push(codINE);
  }

  private addToCache(codINE: string, data: AGOLDataset): void {
    while (this.cache.size >= MAX_CACHED_MUNICIPALITIES) {
      const oldest = this.accessOrder.shift();
      if (oldest) {
        this.cache.delete(oldest);
        console.log(`[AGOLDataService] Evictado de caché: ${oldest}`);
      }
    }
    this.cache.set(codINE, data);
    this.accessOrder.push(codINE);
  }
}

// =============================================================================
// SINGLETON
// =============================================================================

let instance: AGOLDataService | null = null;

export function getAGOLDataService(): AGOLDataService {
  if (!instance) instance = new AGOLDataService();
  return instance;
}

export function resetAGOLDataService(): void {
  if (instance) instance.clearCache();
  instance = null;
}
